import type { ComparisonsDict } from '../en/comparisons';

export const comparisons: ComparisonsDict = {
  nav: 'Jämför',
  received: 'Mottaget avtal',
  standard: 'Standardavtal',
  project: 'Projekt',
  by: 'Av',
  date: 'Datum',
  result: 'Resultat',
  version: (n: number) => `version ${n}`,
  statuses: { queued: 'Väntar…', processing: 'Jämför…', ready: 'Klar', failed: 'Misslyckades' },
  compare: 'Jämför ett dokument',
  ourRole: 'Vi är',
  roles: { auto: 'Utgå från standardavtalet', customer: 'Kund / köpare', supplier: 'Leverantör / säljare', discloser: 'Utlämnande part', recipient: 'Mottagande part', mutual: 'Båda (ömsesidigt)' },
  focus: 'Något att fokusera på? (valfritt)',
  focusPlaceholder: 't.ex. ansvar och dataskydd är viktigast för den här leverantören',
  start: 'Jämför',
  starting: 'Startar…',
  startFailed: 'Kunde inte starta jämförelsen',
  unsupportedFile: 'Bara Word- (.docx) och PDF-filer kan jämföras',
  tooLarge: 'Filen är större än 25 MB',
  prep: {
    pending: 'Inte förberett än',
    preparing: 'Förbereds…',
    ready: (n: number | null) => (n ? `Klart · ${n} punkter` : 'Klart'),
    failed: 'Kunde inte läsas',
  },
  status: 'Status',
  back: 'Jämför',
  progressTitle: 'Jämför avtalen',
  progressHint: 'Det tar oftast ungefär en minut. Du kan lämna sidan; resultatet sparas i projektet.',
  stages: {
    preparing: 'Läser båda dokumenten',
    aligning: 'Matchar punkter',
    analysing: 'Analyserar punkter',
    summarising: 'Sammanfattar',
  },
  failedTitle: 'Jämförelsen kunde inte slutföras',
  retry: 'Kör igen',
  overall: { close: 'Nära vår standard', moderate: 'Några avvikelser att förhandla', far: 'Långt från vår standard' },
  counts: (c: { high: number; medium: number; low: number }) => `${c.high} hög · ${c.medium} medel · ${c.low} låg`,
  incomplete: 'En del av analysen misslyckades, så några punkter kan saknas. Kör igen för ett komplett resultat.',
  topDeviations: 'Viktigast',
  nextSteps: 'Förslag på nästa steg',
  downloadRedline: 'Ladda ner redline',
  download: 'Ladda ner',
  redlineShort: 'Redline (spårade ändringar)',
  cleanShort: 'Ren version',
  reportShort: 'Rapport',
  cleanHint: 'Word-dokument där de godkända förslagen är inarbetade som vanlig text: inga spårade ändringar, inga kommentarer',
  reportHint: 'Word-rapport med alla avvikelser',
  acceptedBar: (n: number) => `${n} förslag godkänt${n === 1 ? '' : 'a'}`,
  redlineHint: 'Word-dokument med de godkända förslagen som spårade ändringar och kommentarer i ditt namn',
  redlinePdf: 'En redline kräver det mottagna avtalet som Word-dokument. Ladda ner rapporten i stället.',
  needAccepted: 'Godkänn först de förslag du vill ha med',
  acceptAll: 'Godkänn alla höga och medel',
  exported: (name: string) => `${name} nedladdad`,
  exportFailed: 'Kunde inte skapa dokumentet',
  skipped: (n: number) => `${n} förslag kunde inte placeras i dokumentet och finns bara i rapporten`,
  rerun: 'Kör igen',
  rerunConfirm: 'Köra jämförelsen igen? Godkända och avfärdade val finns kvar bara på nuvarande resultat.',
  filters: { all: 'Alla', high: 'Hög', medium: 'Medel', low: 'Låg', missing: 'Saknas hos dem', added: 'Tillagt av dem', open: 'Öppna' },
  findingsTitle: 'Avvikelser',
  noFindings: 'Inga avvikelser i det här urvalet.',
  kinds: { changed: 'Ändrad', missing: 'Saknas hos dem', added: 'Tillagd av dem', match: 'Stämmer' },
  directions: { stricter: 'sämre för oss', looser: 'bättre för oss', neutral: 'neutral' },
  risks: { high: 'Hög', medium: 'Medel', low: 'Låg', none: '-' },
  ourStandard: 'Vår standard',
  theirText: 'Deras text',
  suggestion: 'Föreslagen ändring',
  suggestionKinds: { edit: 'Ersätt med', insert: 'Lägg till', delete: 'Ta bort' },
  accept: 'Godkänn',
  accepted: 'Godkänd',
  dismiss: 'Avfärda',
  dismissed: 'Avfärdad',
  reopen: 'Ångra',
  editWording: 'Ändra formulering',
  saveWording: 'Spara',
  cancel: 'Avbryt',
  lowConfidence: 'Osäker: kontrollera den här',
  viewOnly: 'Du kan se jämförelsen; bara de som kan redigera projektet kan godkänna eller avfärda avvikelser.',
  matchesTitle: (n: number) => `Stämmer med vår standard (${n})`,
  clauseRefs: (theirs: string | null, ours: string | null) => [theirs && `Deras ${theirs}`, ours && `vår ${ours}`].filter(Boolean).join(' · '),
  updateFailed: 'Kunde inte spara',
  deleteComparison: 'Ta bort jämförelsen',
  deleted: 'Jämförelsen borttagen',
  // Ingångar: projektfil, chatt, fortsätt i chatten
  compareFile: 'Jämför med standardavtal…',
  fileDialogTitle: (file: string) => `Jämför “${file}” med ett standardavtal`,
  fileDialogDescription: 'Queri jämför dokumentet klausul för klausul med standardavtalet du väljer och markerar vad som bör förhandlas. Filen ligger kvar där den är.',
  pickStandard: 'Standardavtal',
  pickStandardPlaceholder: 'Välj ett standardavtal',
  noStandards: 'Organisationen har inga standardavtal ännu. Lägg till ett under Playbooks → Standardavtal.',
  standardsFailed: 'Kunde inte hämta standardavtalen',
  continueInChat: 'Fortsätt i chatten',
  openingChat: 'Öppnar chatten…',
  chatFailed: 'Kunde inte öppna chatten',
  openComparison: 'Öppna jämförelsen',
  cardTitle: (standard: string) => `Jämförelse med “${standard}”`,
  cardUnavailable: 'Jämförelsen finns inte längre.',
  cardFailed: 'Jämförelsen kunde inte slutföras.',
  cardFile: (file: string) => `Mottaget: ${file}`,
  chatBanner: 'Du diskuterar en jämförelse. Be om ändrade formuleringar så visas de som ändringsförslag i det mottagna avtalet.',
  chatBannerLink: 'Öppna jämförelsen',
  chatStarted: (file: string, standard: string) => `Jämför “${file}” med standardavtalet “${standard}”`,
  // Våra standardavtal (biblioteket på Jämför-sidan)
  lib: {
    intro: 'Organisationens egna avtalsmallar. Jämför granskar avtal ni får mot dem, punkt för punkt.',
    title: 'Våra standardavtal',
    upload: 'Ladda upp',
    empty: 'Inga standardavtal ännu',
    emptyHint: 'Ladda upp era mallar (Word eller PDF) så att alla i organisationen utgår från samma standard.',
    loadFailed: 'Det gick inte att läsa in standardavtalen',
    name: 'Namn',
    agreementType: 'Avtalstyp',
    addedBy: 'Tillagd av',
    added: 'Tillagd',
    download: 'Ladda ner',
    edit: 'Redigera uppgifter',
    delete: 'Radera',
    downloadFailed: 'Det gick inte att ladda ner filen',
    uploadDialogTitle: 'Ladda upp standardavtal',
    uploadDialogDescription: 'Word (.docx) eller PDF, högst 25 MB. Alla i organisationen kommer att se det.',
    chooseFile: 'Välj en fil eller släpp den här',
    fileTypes: '.docx eller .pdf, högst 25 MB',
    namePlaceholder: 't.ex. Standardavtal för SaaS',
    typePlaceholder: 't.ex. SaaS, NDA, ramavtal',
    descriptionLabel: 'Anteckningar (valfritt)',
    descriptionPlaceholder: 'När det ska användas, version, ansvarig…',
    cancel: 'Avbryt',
    save: 'Spara',
    uploading: 'Laddar upp…',
    uploaded: (name: string) => `"${name}" har lagts till`,
    uploadFailed: 'Uppladdningen misslyckades',
    unsupportedFile: 'Endast Word (.docx) och PDF stöds',
    tooLarge: 'Filen är större än 25 MB',
    editDialogTitle: 'Redigera standardavtal',
    saved: 'Sparat',
    saveFailed: 'Det gick inte att spara ändringarna',
    deleteTitle: 'Radera standardavtal',
    deleteDescription: (name: string) =>
      `Radera "${name}" för alla i organisationen? Filen tas bort och det går inte att ångra.`,
    deleted: 'Standardavtalet har raderats',
    deleteFailed: 'Det gick inte att radera standardavtalet',
    openMenu: 'Öppna meny',
    moreActions: 'Fler åtgärder',
  },
  // Jämför-sidan
  cp: {
    title: 'Jämför',
    subtitle: 'Kontrollera ett avtal ni har fått mot ett av organisationens standardavtal.',
    dropTitle: 'Släpp avtalet här',
    dropHint: 'Word eller PDF, högst 25 MB. Klicka för att välja en fil.',
    dropAria: 'Släpp avtalet som ska jämföras här, eller tryck Enter för att välja en fil. Word eller PDF.',
    dropRelease: 'Släpp för att använda filen',
    tabCompare: 'Jämför',
    tabStandards: 'Våra standardavtal',
    chosenFile: 'Avtalet ni har fått',
    step1Title: 'Vårt standardavtal',
    step1Hint: 'Det avtal ni jämför mot. Bara standardavtal som är klara kan väljas.',
    step2Title: 'Avtalet ni har fått',
    step2Label: 'Avtalet ni har fått från motparten',
    step3Title: 'Vår roll och alternativ',
    standardsEmpty: 'Inga standardavtal ännu. Ladda upp organisationens standardavtal att jämföra mot.',
    uploadFirst: 'Ladda upp ett standardavtal',
    uploadNew: 'Ladda upp ett nytt standardavtal',
    uploadingStandard: 'Laddar upp…',
    manageStandards: 'Hantera standardavtal',
    retry: 'Försök igen',
    retried: 'Förbereder igen…',
    retryFailed: 'Det gick inte att starta förberedelsen igen',
    reasonChoose: 'Välj ett standardavtal som är klart',
    reasonWaiting: (name: string) => `Väntar på att “${name}” ska förberedas…`,
    reasonFailed: (name: string) => `“${name}” kunde inte läsas. Försök igen eller välj ett annat standardavtal`,
    reasonFile: 'Lägg till avtalet ni har fått',
    looksLikeStandard: (name: string) => `Det här ser ut som ert standardavtal “${name}”. Jämför behöver avtalet ni har fått.`,
    uploadAsStandard: 'Ladda upp det som standardavtal istället',
    compareAnyway: 'Jämför ändå',
    standardsLabel: 'Standardavtal',
    changeFile: 'Välj en annan fil',
    removeFile: 'Ta bort filen',
    orPick: 'eller',
    pickFromProject: 'Välj en fil från ett projekt',
    pickerTitle: 'Välj ett dokument från ett projekt',
    pickerDescription: 'Filen ligger kvar i sitt projekt. Jämförelsen är fortfarande privat för dig.',
    pickerProject: 'Projekt',
    pickerProjectPlaceholder: 'Välj ett projekt',
    pickerNoProjects: 'Du har inga projekt med Word- eller PDF-filer ännu.',
    pickerNoFiles: 'Projektet har inga Word- eller PDF-filer.',
    pickerFile: 'Dokument',
    pickerUse: 'Använd dokumentet',
    pickerFailed: 'Kunde inte hämta projekten',
    suggested: 'föreslaget',
    chooseStandard: 'Välj ett standardavtal',
    weAre: 'Vi är',
    options: 'Alternativ',
    run: 'Jämför',
    quickTitle: 'Snabbkontroller',
    quickSubtitle: 'Dina sparade jämförelseflöden. Släpp ett dokument på ett av dem för att köra det direkt.',
    quickDrop: 'Släpp ett dokument här eller klicka för att välja',
    quickAria: (name: string) => `${name}: släpp ett dokument eller tryck Enter för att välja en fil och köra kontrollen`,
    quickStandard: (name: string) => `Mot ${name}`,
    quickNoStandard: 'Flödet behöver ett standardavtal som är klart. Ändra det under Playbooks → Arbetsflöden.',
    quickRunning: 'Startar…',
    mineTitle: 'Mina jämförelser',
    mineSubtitle: 'Bara du kan se dina jämförelser.',
    mineEmpty: 'Inga jämförelser ännu. Släpp ett avtal ovan för att köra din första.',
    mineFailed: 'Kunde inte hämta dina jämförelser',
    showMore: (n: number) => `Visa ${n} till`,
    open: 'Öppna',
    standardGone: 'Standardavtalet eller filen finns inte längre.',
    compareAnother: 'Jämför ett annat dokument',
    compareAnotherHint: 'Öppnar Jämför med samma standardavtal, roll och fokus ifyllda',
  },
};
